import { useCallback, useEffect, useMemo, useState } from 'react';
import { ErrorAlert, Loading } from '../components/ui';

/**
 * คอนโซลของผู้ดูแลแพลตฟอร์ม
 *
 * หน้านี้ใช้ token คนละชนิดกับหน้าอื่นทั้งหมด (`typ: 'platform'`) จึงเก็บแยก
 * ไม่ปนกับ token ของเจ้าหน้าที่ — คนคนเดียวเปิดสองแท็บพร้อมกันได้โดยไม่ตีกัน
 *
 * ที่นี่เห็นได้แค่ **จำนวน** ไม่ใช่ **เนื้อหา** ตั้งใจให้เป็นแบบนั้น:
 * เราเป็นผู้ประมวลผลข้อมูลให้มูลนิธิ ไม่ใช่เจ้าของข้อมูล
 *
 * ─── หน้านี้ตอบคำถามอะไร ──────────────────────────────────────────────
 *
 * ไม่ใช่ "มีมูลนิธิอะไรอยู่บ้าง" (รายชื่อเฉย ๆ ตอบได้ด้วยคำสั่ง SQL บรรทัดเดียว)
 * แต่คือ "**รายไหนเปิดไว้แล้วยังใช้งานจริงไม่ได้**" มูลนิธิที่ตั้งค่าไม่ครบจะเงียบสนิท
 * ไม่มีใครมาบ่น เพราะเจ้าหน้าที่ของเขายังเข้าระบบไม่ได้ด้วยซ้ำ
 * รายการด้านล่างจึงเรียง "ติดอยู่" ขึ้นก่อนเสมอ และบอกด้วยว่าต้องไปแก้ที่หน้าไหน
 */

const TOKEN_KEY = 'platform_token';

/** สถานะการตั้งค่าที่ API คำนวณมาให้ — เป็น boolean ล้วน ไม่มีเนื้อหาของค่าติดมา */
interface Readiness {
  orgProfile: boolean;
  logo: boolean;
  storage: boolean;
  richMenus: boolean;
  bankAccount: boolean;
  staffLogin: boolean;
  staffMessaging: boolean;
  publicLogin: boolean;
  publicMessaging: boolean;
}

interface OrgRow {
  id: string;
  slug: string;
  nameTh: string;
  status: string;
  /** null = ใช้ลิงก์บนโดเมนกลาง ซึ่งเป็นกรณีปกติและใช้งานได้ครบ */
  customDomain: string | null;
  createdAt: string;
  suspendedAt: string | null;
  readiness: Readiness;
  counts: { staff: number; beneficiaries: number; donations: number };
}

interface Usage {
  counts: Record<string, number>;
  storageBytes: number;
}

interface QuotaLine {
  key: string;
  label: string;
  used: number;
  limit: number;
  state: 'ok' | 'warn' | 'over';
  blocks: boolean;
}

interface QuotaView {
  lines: QuotaLine[];
  anyOver: boolean;
  anyWarn: boolean;
  trend: { takenOn: string; totalRows: number }[];
}

interface ClosureView {
  slug: string;
  closure: { requestedAt: string | null; exportDownloadedAt: string | null; purgedAt: string | null };
  canPurge: boolean;
  blockers: string[];
  canPurgeAfter: string | null;
  willShredKey: boolean;
}

/**
 * ขั้นตอนการตั้งค่าทั้งหมด เรียงตามลำดับที่ควรทำจริง
 *
 * `required` = ถ้าขาดข้อนี้ มูลนิธิ **ใช้งานจริงไม่ได้** ไม่ใช่แค่ไม่สวย
 *   - staffLogin/staffMessaging ขาด → เจ้าหน้าที่เข้าแอปไม่ได้เลย
 *   - bankAccount ขาด → รับบริจาคไม่ได้ (ไม่มี QR พร้อมเพย์)
 *   - orgProfile ขาด → ใบเสร็จออกมาไม่มีชื่อ/เลขประจำตัวผู้เสียภาษี
 * ที่เหลือขาดได้ ระบบยังเดินต่อ — โลโก้ ที่เก็บไฟล์สำรอง เมนู และช่องทางผู้บริจาค
 *
 * `where` คือ **หน้าที่ผู้ดูแลของมูลนิธินั้นต้องไปกรอก** ไม่ใช่หน้าของเรา —
 * เราตั้งค่าแทนเขาไม่ได้ เพราะต้องใช้ความลับจาก LINE Console ของเขาเอง
 */
const STEPS: { key: keyof Readiness; label: string; required: boolean; where: string }[] = [
  { key: 'orgProfile', label: 'ข้อมูลมูลนิธิ (ชื่อ ที่อยู่ เลขผู้เสียภาษี)', required: true, where: 'ตั้งค่าระบบ › ข้อมูลมูลนิธิ' },
  { key: 'bankAccount', label: 'บัญชีธนาคาร / พร้อมเพย์', required: true, where: 'ตั้งค่าระบบ › บัญชีธนาคาร' },
  { key: 'staffLogin', label: 'LINE Login ของเจ้าหน้าที่', required: true, where: 'ตั้งค่าระบบ › ช่องทาง LINE' },
  { key: 'staffMessaging', label: 'LINE OA ของเจ้าหน้าที่', required: true, where: 'ตั้งค่าระบบ › ช่องทาง LINE' },
  { key: 'publicLogin', label: 'LINE Login ของผู้บริจาค', required: false, where: 'ตั้งค่าระบบ › ช่องทาง LINE' },
  { key: 'publicMessaging', label: 'LINE OA ของผู้บริจาค', required: false, where: 'ตั้งค่าระบบ › ช่องทาง LINE' },
  { key: 'logo', label: 'โลโก้', required: false, where: 'ตั้งค่าระบบ › ข้อมูลมูลนิธิ' },
  { key: 'richMenus', label: 'เมนูใน LINE', required: false, where: 'ตั้งค่าระบบ › ช่องทาง LINE' },
  { key: 'storage', label: 'สำรองไฟล์ขึ้น Google Drive', required: false, where: 'ตั้งค่าระบบ › ที่เก็บไฟล์' },
];

const COUNT_LABEL: Record<string, string> = {
  staff: 'เจ้าหน้าที่',
  beneficiary: 'ผู้รับความช่วยเหลือ',
  donor: 'ผู้บริจาค',
  donation: 'รายการบริจาค',
  aid_case: 'เคส',
  visit: 'งานลงพื้นที่',
  media_object: 'ไฟล์',
  message_outbox: 'ข้อความ',
};

const STATUS_LABEL: Record<string, string> = {
  active: 'ใช้งานอยู่',
  suspended: 'ระงับชั่วคราว',
  closed: 'ปิดแล้ว',
};

type Filter = 'all' | 'incomplete' | 'suspended';

function missingRequired(o: OrgRow) {
  return STEPS.filter((s) => s.required && !o.readiness[s.key]);
}

/** เรียก API ด้วย token ของแพลตฟอร์ม ไม่ใช่ของเจ้าหน้าที่ */
async function platformApi<T>(path: string, opts: { method?: string; body?: unknown } = {}) {
  const token = sessionStorage.getItem(TOKEN_KEY);
  const base = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api';
  const res = await fetch(`${base}${path}`, {
    method: opts.method ?? 'GET',
    headers: {
      'content-type': 'application/json',
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
    ...(opts.body !== undefined ? { body: JSON.stringify(opts.body) } : {}),
  });
  const text = await res.text();
  const data: unknown = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const msg =
      (data as { error?: { message?: string } })?.error?.message ?? `คำขอไม่สำเร็จ (${res.status})`;
    if (res.status === 401) sessionStorage.removeItem(TOKEN_KEY);
    throw new Error(msg);
  }
  return data as T;
}

export default function Platform() {
  const [orgs, setOrgs] = useState<OrgRow[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [usage, setUsage] = useState<Record<string, Usage>>({});
  const [quota, setQuota] = useState<Record<string, QuotaView>>({});
  const [closure, setClosure] = useState<Record<string, ClosureView>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [signedIn, setSignedIn] = useState(Boolean(sessionStorage.getItem(TOKEN_KEY)));

  const load = useCallback(async () => {
    if (!sessionStorage.getItem(TOKEN_KEY)) return;
    setError(null);
    try {
      setOrgs((await platformApi<{ data: OrgRow[] }>('/platform/orgs')).data);
    } catch (e) {
      setError(e);
      setSignedIn(Boolean(sessionStorage.getItem(TOKEN_KEY)));
    }
  }, []);

  // แลกโค้ดที่ callback ส่งกลับมาบน URL เป็น token จริง (ดู routes/platform.ts)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    if (params.get('login') !== 'ok' || !code) {
      void load();
      return;
    }
    void (async () => {
      try {
        const res = await platformApi<{ token: string }>('/platform-auth/sso/exchange', {
          method: 'POST',
          body: { code },
        });
        sessionStorage.setItem(TOKEN_KEY, res.token);
        setSignedIn(true);
        window.history.replaceState({}, '', window.location.pathname);
        await load();
      } catch (e) {
        setError(e);
      }
    })();
  }, [load]);

  async function signIn() {
    try {
      const res = await platformApi<{ authorizeUrl: string }>('/platform-auth/sso/start', {
        method: 'POST',
      });
      window.location.href = res.authorizeUrl;
    } catch (e) {
      setError(e);
    }
  }

  async function createOrg(form: HTMLFormElement) {
    const d = new FormData(form);
    setBusy(true);
    setError(null);
    setFlash(null);
    try {
      const res = await platformApi<{ slug: string; nextSteps: string[] }>('/platform/orgs', {
        method: 'POST',
        body: {
          slug: String(d.get('slug') ?? '').trim(),
          nameTh: String(d.get('nameTh') ?? '').trim(),
          adminEmail: String(d.get('adminEmail') ?? '').trim(),
          adminName: String(d.get('adminName') ?? '').trim(),
        },
      });
      setFlash(`เปิดมูลนิธิ "${res.slug}" แล้ว — ${res.nextSteps.join(' · ')}`);
      form.reset();
      setShowForm(false);
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  async function setStatus(org: OrgRow, status: string) {
    const reason = window.prompt(`เหตุผลที่เปลี่ยนสถานะของ "${org.nameTh}"`)?.trim();
    if (!reason || reason.length < 4) return;
    setError(null);
    try {
      await platformApi(`/platform/orgs/${org.id}/status`, {
        method: 'PATCH',
        body: { status, reason },
      });
      await load();
    } catch (e) {
      setError(e);
    }
  }

  /**
   * โดเมนเป็นของเสริม — ปุ่มนี้จึงมีทั้ง "ตั้ง" และ "ยกเลิก"
   * และการยกเลิกต้องไม่ทำให้เสียฟีเจอร์อะไรเลย
   */
  async function setDomain(org: OrgRow) {
    const current = org.customDomain ?? '';
    const input = window.prompt(
      `โดเมนของ "${org.nameTh}" (เว้นว่าง = ใช้ลิงก์บนโดเมนกลาง ซึ่งใช้งานได้ครบเหมือนกัน)`,
      current,
    );
    if (input === null) return;

    setError(null);
    setFlash(null);
    try {
      const res = await platformApi<{ note: string }>(`/platform/orgs/${org.id}/domain`, {
        method: 'PATCH',
        body: { customDomain: input.trim() || null },
      });
      setFlash(res.note);
      await load();
    } catch (e) {
      setError(e);
    }
  }

  async function showUsage(org: OrgRow) {
    try {
      const data = await platformApi<Usage>(`/platform/orgs/${org.id}/usage`);
      setUsage((u) => ({ ...u, [org.id]: data }));
    } catch (e) {
      setError(e);
    }
  }

  async function showQuota(org: OrgRow) {
    try {
      const view = await platformApi<QuotaView>(`/platform/orgs/${org.id}/quota`);
      setQuota((q) => ({ ...q, [org.id]: view }));
    } catch (e) {
      setError(e);
    }
  }

  /** ตั้งเพดานเฉพาะราย — เว้นว่าง = กลับไปใช้ค่าปริยาย, 0 = ไม่จำกัดอย่างจงใจ */
  async function setLimit(org: OrgRow, key: string, label: string, current: number) {
    const input = window.prompt(
      `เพดาน${label}ของ "${org.nameTh}"\n\nเว้นว่าง = ใช้ค่าปริยายของแพลตฟอร์ม\n0 = ไม่จำกัด`,
      String(current),
    );
    if (input === null) return;
    const trimmed = input.trim();
    if (trimmed !== '' && !/^\d+$/.test(trimmed)) {
      setError(new Error('ใส่ได้เฉพาะตัวเลขจำนวนเต็ม หรือเว้นว่างเพื่อใช้ค่าปริยาย'));
      return;
    }

    setError(null);
    try {
      await platformApi(`/platform/orgs/${org.id}/quota`, {
        method: 'PATCH',
        body: { [key]: trimmed === '' ? null : Number(trimmed) },
      });
      setFlash(`ตั้งเพดาน${label}ของ "${org.nameTh}" แล้ว`);
      await showQuota(org);
    } catch (e) {
      setError(e);
    }
  }

  async function showClosure(org: OrgRow) {
    try {
      const view = await platformApi<ClosureView>(`/platform/orgs/${org.id}/closure`);
      setClosure((c) => ({ ...c, [org.id]: view }));
    } catch (e) {
      setError(e);
    }
  }

  /**
   * ลบข้อมูลถาวร — ย้อนกลับไม่ได้
   *
   * ให้พิมพ์ slug เองแทนการกด "ตกลง" เพราะหน้านี้เป็นรายการหลายรายเรียงกัน
   * และการกดผิดแถวคือวิธีพลาดที่พบบ่อยที่สุดของหน้าจอแบบนี้
   * เซิร์ฟเวอร์ตรวจซ้ำอีกชั้นเสมอ — หน้าจอไม่ใช่เขตแดนความปลอดภัย
   */
  async function purge(org: OrgRow) {
    const view = closure[org.id];
    if (!view?.canPurge) return;

    const typed = window.prompt(
      `ลบข้อมูลทั้งหมดของ "${org.nameTh}" ถาวร\n\n` +
        (view.willShredKey
          ? 'คีย์จะถูกทิ้ง ข้อมูลที่เข้ารหัสไว้กู้ไม่ได้อีกแม้มีสำเนาฐานข้อมูลเก่า\n\n'
          : 'มูลนิธินี้ใช้คีย์ของแพลตฟอร์ม จึงทิ้งคีย์ไม่ได้ — ลบด้วยการลบแถวเท่านั้น\n\n') +
        `พิมพ์รหัสมูลนิธิ "${org.slug}" เพื่อยืนยัน`,
    );
    if (typed === null) return;

    const reason = window.prompt('เหตุผล (อย่างน้อย 10 ตัวอักษร)')?.trim();
    if (!reason || reason.length < 10) return;

    setError(null);
    try {
      const res = await platformApi<{ totalDeleted: number; keyNote: string }>(
        `/platform/orgs/${org.id}/purge`,
        { method: 'POST', body: { confirmSlug: typed.trim(), reason } },
      );
      setFlash(`ลบข้อมูล ${res.totalDeleted.toLocaleString('th-TH')} แถวแล้ว — ${res.keyNote}`);
      await load();
    } catch (e) {
      setError(e);
    }
  }

  /**
   * เรียง "ยังตั้งค่าไม่ครบ" ขึ้นก่อนโดยตั้งใจ
   * ถ้าเรียงตามวันที่สร้าง มูลนิธิที่ติดอยู่จะจมอยู่ท้ายรายการเมื่อมีลูกค้าเยอะขึ้น
   * ซึ่งเป็นตอนที่เราต้องการให้มันเด่นที่สุดพอดี
   */
  const shown = useMemo(() => {
    if (!orgs) return [];
    const needle = q.trim().toLowerCase();
    return orgs
      .filter((o) => {
        if (filter === 'incomplete' && missingRequired(o).length === 0) return false;
        if (filter === 'suspended' && o.status === 'active') return false;
        if (!needle) return true;
        return (
          o.nameTh.toLowerCase().includes(needle) ||
          o.slug.includes(needle) ||
          (o.customDomain ?? '').includes(needle)
        );
      })
      .sort((a, b) => {
        const d = missingRequired(b).length - missingRequired(a).length;
        return d !== 0 ? d : a.nameTh.localeCompare(b.nameTh, 'th');
      });
  }, [orgs, q, filter]);

  const stuck = orgs?.filter((o) => missingRequired(o).length > 0).length ?? 0;

  if (!signedIn) {
    return (
      <>
        <div className="page-head">
          <div>
            <h1>ดูแลแพลตฟอร์ม</h1>
            <p>สำหรับผู้ดูแลระบบส่วนกลางเท่านั้น</p>
          </div>
        </div>
        <ErrorAlert error={error} />
        <div className="card">
          <p className="hint">
            บัญชีนี้แยกจากบัญชีเจ้าหน้าที่มูลนิธิโดยสิ้นเชิง — เข้าที่นี่แล้วยังเปิดข้อมูล
            ผู้รับความช่วยเหลือหรือผู้บริจาคของมูลนิธิไหนไม่ได้
          </p>
          <div className="btn-row">
            <button className="btn" onClick={() => void signIn()}>
              เข้าด้วย Google
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>มูลนิธิในระบบ</h1>
          <p>
            {orgs
              ? stuck > 0
                ? `ทั้งหมด ${orgs.length} มูลนิธิ · ${stuck} รายยังตั้งค่าไม่ครบ ใช้งานจริงไม่ได้`
                : `ทั้งหมด ${orgs.length} มูลนิธิ · ตั้งค่าครบทุกราย`
              : 'กำลังโหลด…'}
          </p>
        </div>
        <div className="btn-row">
          <button className="btn" onClick={() => setShowForm((v) => !v)}>
            {showForm ? 'ปิดฟอร์ม' : 'เปิดมูลนิธิใหม่'}
          </button>
        </div>
      </div>

      <ErrorAlert error={error} />
      {flash && <div className="alert success">{flash}</div>}

      {showForm && (
        <form
          className="card"
          onSubmit={(e) => {
            e.preventDefault();
            void createOrg(e.currentTarget);
          }}
        >
          <h2>เปิดมูลนิธิใหม่</h2>
          <p className="hint">
            ระบบจะคัดลอกประเภทความช่วยเหลือ ทีม แบบฟอร์มสำรวจ และค่าตั้งค่าตั้งต้นให้อัตโนมัติ
            ส่วนโลโก้ บัญชีธนาคาร และ LINE ให้ผู้ดูแลของเขากรอกเอง
          </p>

          <label>
            รหัสมูลนิธิ (ใช้ในลิงก์) <span className="hint">a-z 0-9 และขีด เช่น ruamjai</span>
            <input name="slug" required pattern="[a-z0-9][a-z0-9-]{1,38}" />
          </label>
          <label>
            ชื่อมูลนิธิ
            <input name="nameTh" required minLength={2} />
          </label>
          <label>
            อีเมล Google ของผู้ดูแลคนแรก{' '}
            <span className="hint">คนนี้เข้าระบบได้ทันทีหลังกดสร้าง</span>
            <input name="adminEmail" type="email" required />
          </label>
          <label>
            ชื่อผู้ดูแลคนแรก
            <input name="adminName" required minLength={2} />
          </label>

          <div className="btn-row">
            <button className="btn" disabled={busy}>
              {busy ? 'กำลังเปิด…' : 'เปิดมูลนิธิ'}
            </button>
          </div>
        </form>
      )}

      <div className="toolbar">
        <div className="field grow">
          <label htmlFor="pq">ค้นหา</label>
          <input
            id="pq"
            type="search"
            placeholder="ชื่อมูลนิธิ รหัส หรือโดเมน"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="btn-row">
          {(
            [
              ['all', 'ทั้งหมด'],
              ['incomplete', `ยังตั้งค่าไม่ครบ${stuck ? ` (${stuck})` : ''}`],
              ['suspended', 'ระงับ/ปิดแล้ว'],
            ] as [Filter, string][]
          ).map(([key, label]) => (
            <button
              key={key}
              className={filter === key ? 'btn' : 'btn ghost'}
              onClick={() => setFilter(key)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {!orgs ? (
        <Loading />
      ) : shown.length === 0 ? (
        <div className="card">
          <p className="hint">ไม่มีมูลนิธิที่ตรงกับเงื่อนไขนี้</p>
        </div>
      ) : (
        shown.map((o) => {
          const missing = missingRequired(o);
          const optionalMissing = STEPS.filter((s) => !s.required && !o.readiness[s.key]);
          return (
            <div className="card" key={o.id}>
              <h2>
                {o.nameTh} <span className="badge">{o.slug}</span>{' '}
                <span className={`badge ${o.status === 'active' ? 'green' : 'amber'}`}>
                  {STATUS_LABEL[o.status] ?? o.status}
                </span>{' '}
                {missing.length > 0 ? (
                  <span className="badge red">ยังขาด {missing.length} อย่าง</span>
                ) : (
                  <span className="badge blue">ตั้งค่าครบ</span>
                )}
              </h2>

              <p className="hint">
                ลิงก์: <span className="mono">/o/{o.slug}/</span>
                {o.customDomain ? (
                  <>
                    {' · '}โดเมนของตัวเอง <span className="mono">{o.customDomain}</span>
                  </>
                ) : (
                  ' · ยังไม่มีโดเมนของตัวเอง (ใช้งานได้ครบตามปกติ)'
                )}
                {' · '}เปิดเมื่อ {new Date(o.createdAt).toLocaleDateString('th-TH')}
              </p>

              <p className="hint">
                เจ้าหน้าที่ {o.counts.staff.toLocaleString('th-TH')} คน · ผู้รับความช่วยเหลือ{' '}
                {o.counts.beneficiaries.toLocaleString('th-TH')} คน · รายการบริจาค{' '}
                {o.counts.donations.toLocaleString('th-TH')} รายการ
              </p>

              {missing.length > 0 && (
                <div className="alert warn">
                  <strong>ยังใช้งานจริงไม่ได้</strong> — ผู้ดูแลของมูลนิธินี้ต้องกรอกให้ครบก่อน
                  (เราตั้งให้แทนไม่ได้ เพราะต้องใช้ความลับจาก LINE Console และเลขบัญชีของเขาเอง)
                  <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
                    {missing.map((s) => (
                      <li key={s.key}>
                        {s.label} <span className="hint">— {s.where}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {missing.length === 0 && optionalMissing.length > 0 && (
                <p className="hint">
                  ใช้งานได้ครบแล้ว ส่วนที่ยังไม่ได้ตั้ง (ไม่บังคับ):{' '}
                  {optionalMissing.map((s) => s.label).join(' · ')}
                </p>
              )}

              <div className="btn-row">
                <button
                  className="btn ghost"
                  onClick={() => setExpanded((e) => ({ ...e, [o.id]: !e[o.id] }))}
                >
                  {expanded[o.id] ? 'ซ่อนรายการตั้งค่า' : 'ดูรายการตั้งค่าทั้งหมด'}
                </button>
                <button className="btn ghost" onClick={() => void showUsage(o)}>
                  ดูปริมาณการใช้งาน
                </button>
                <button className="btn ghost" onClick={() => void showQuota(o)}>
                  เพดานการใช้งาน
                </button>
                <button className="btn ghost" onClick={() => void showClosure(o)}>
                  สถานะการปิด
                </button>
                <button className="btn ghost" onClick={() => void setDomain(o)}>
                  {o.customDomain ? 'เปลี่ยน/ยกเลิกโดเมน' : 'ตั้งโดเมนของตัวเอง'}
                </button>
                {o.status === 'active' ? (
                  <button className="btn ghost" onClick={() => void setStatus(o, 'suspended')}>
                    ระงับชั่วคราว
                  </button>
                ) : o.status === 'suspended' ? (
                  <button className="btn ghost" onClick={() => void setStatus(o, 'active')}>
                    เปิดใช้งานคืน
                  </button>
                ) : null}
              </div>

              {expanded[o.id] && (
                <ul className="hint" style={{ paddingLeft: 20, marginTop: 10 }}>
                  {STEPS.map((s) => (
                    <li key={s.key}>
                      {o.readiness[s.key] ? '✓' : '✗'} {s.label}
                      {!s.required && ' (ไม่บังคับ)'}
                      {!o.readiness[s.key] && <span className="hint"> — {s.where}</span>}
                    </li>
                  ))}
                </ul>
              )}

              {quota[o.id] && (
                <div style={{ marginTop: 10 }}>
                  <table>
                    <thead>
                      <tr>
                        <th>เพดาน</th>
                        <th style={{ textAlign: 'right' }}>ใช้ไป</th>
                        <th style={{ textAlign: 'right' }}>เพดาน</th>
                        <th>สถานะ</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {quota[o.id]!.lines.map((l) => (
                        <tr key={l.key}>
                          <td>{l.label}</td>
                          <td style={{ textAlign: 'right' }}>{l.used.toLocaleString('th-TH')}</td>
                          <td style={{ textAlign: 'right' }}>
                            {l.limit === 0 ? 'ไม่จำกัด' : l.limit.toLocaleString('th-TH')}
                          </td>
                          <td>
                            {l.state === 'ok' ? (
                              <span className="badge green">ปกติ</span>
                            ) : (
                              <span className={`badge ${l.state === 'over' ? 'red' : 'amber'}`}>
                                {l.state === 'over' ? 'เกิน' : 'ใกล้เต็ม'}
                                {!l.blocks && ' (ไม่บล็อก)'}
                              </span>
                            )}
                          </td>
                          <td>
                            <button
                              className="link"
                              onClick={() => void setLimit(o, l.key, l.label, l.limit)}
                            >
                              แก้
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {quota[o.id]!.trend.length > 1 && (
                    <p className="hint" style={{ marginTop: 8 }}>
                      แนวโน้ม 30 วัน:{' '}
                      {quota[o.id]!.trend[0]!.totalRows.toLocaleString('th-TH')} →{' '}
                      {quota[o.id]!.trend.at(-1)!.totalRows.toLocaleString('th-TH')} แถว
                      {' · '}
                      เพิ่มเฉลี่ยวันละ{' '}
                      {Math.round(
                        (quota[o.id]!.trend.at(-1)!.totalRows - quota[o.id]!.trend[0]!.totalRows) /
                          Math.max(1, quota[o.id]!.trend.length - 1),
                      ).toLocaleString('th-TH')}{' '}
                      แถว
                    </p>
                  )}
                </div>
              )}

              {closure[o.id] && (
                <div className="alert warn" style={{ marginTop: 10 }}>
                  {closure[o.id]!.closure.purgedAt ? (
                    <>ลบข้อมูลถาวรไปแล้วเมื่อ {new Date(closure[o.id]!.closure.purgedAt!).toLocaleString('th-TH')}</>
                  ) : !closure[o.id]!.closure.requestedAt ? (
                    <>
                      มูลนิธินี้ยังไม่ได้ยื่นคำขอปิด — <strong>เราสั่งลบเองไม่ได้</strong>{' '}
                      การตัดสินใจปิดเป็นสิทธิ์ของเจ้าของข้อมูล ต้องให้ super_admin ของเขายื่นคำขอเอง
                    </>
                  ) : (
                    <>
                      <strong>มีคำขอปิดค้างอยู่</strong>
                      {closure[o.id]!.canPurge ? (
                        <>
                          {' '}— ผ่านครบทุกเงื่อนไขแล้ว
                          {!closure[o.id]!.willShredKey &&
                            ' (มูลนิธิแรกของระบบ — ทิ้งคีย์ไม่ได้ ลบได้แค่ตัวแถว)'}
                          <div className="btn-row" style={{ marginTop: 8 }}>
                            <button className="btn danger" onClick={() => void purge(o)}>
                              ลบข้อมูลถาวร
                            </button>
                          </div>
                        </>
                      ) : (
                        <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
                          {closure[o.id]!.blockers.map((b) => (
                            <li key={b}>{b}</li>
                          ))}
                        </ul>
                      )}
                    </>
                  )}
                </div>
              )}

              {usage[o.id] && (
                <ul className="hint" style={{ paddingLeft: 20, marginTop: 10 }}>
                  {Object.entries(usage[o.id]!.counts).map(([k, v]) => (
                    <li key={k}>
                      {COUNT_LABEL[k] ?? k}: {v.toLocaleString('th-TH')}
                    </li>
                  ))}
                  <li>
                    ขนาดไฟล์รวม:{' '}
                    {(usage[o.id]!.storageBytes / 1_048_576).toLocaleString('th-TH', {
                      maximumFractionDigits: 1,
                    })}{' '}
                    MB
                  </li>
                </ul>
              )}
            </div>
          );
        })
      )}

      <div className="alert info">
        <strong>โดเมนของตัวเองเป็นของเสริม ไม่ใช่สิ่งจำเป็น</strong> —
        ทุกฟีเจอร์ใช้งานได้ครบผ่านลิงก์ <span className="mono">/o/&lt;รหัสมูลนิธิ&gt;/</span>
        อยู่แล้ว มูลนิธิเล็กจึงไม่ต้องเสียค่าโดเมนก็ได้ ส่วนมูลนิธิที่รับบริจาคเยอะ
        การมีโดเมนของตัวเองช่วยเรื่องความน่าเชื่อถือตอนส่งลิงก์ให้ผู้บริจาค
        <br />
        ตั้งโดเมนแล้วต้องไปผูกกับโปรเจกต์ Pages ในแดชบอร์ด Cloudflare และชี้ DNS ด้วย
      </div>

      <div className="alert info">
        ตัวเลขทั้งหมดในหน้านี้เป็น<strong>จำนวน</strong>เท่านั้น ไม่มีชื่อคน เลขบัญชี
        หรือเนื้อหาของการตั้งค่าใด ๆ — การเข้าดู<strong>ข้อมูล</strong>ของมูลนิธิต้องขอสิทธิ์
        ชั่วคราวพร้อมเหตุผล และผู้ดูแลของเขาจะเห็นในบันทึกการใช้งานของเขาเองว่ามีคนนอกเข้ามาดูตอนไหน
      </div>
    </>
  );
}
